import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  useKlantenStore,
  type CreateCustomerInput,
  type DuplicateCustomerMatch,
} from '../../stores/klanten-store';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type ModalStep = 'type' | 'form';
type CustomerType = 'Person' | 'Business';

interface FormState {
  // Person én Business: contactpersoon-velden
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobile: string;
  // Business-only
  companyName: string;
  // Adres (gedeeld)
  street: string;
  postcode: string;
  city: string;
}

const EMPTY_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  mobile: '',
  companyName: '',
  street: '',
  postcode: '',
  city: '',
};

const POSTCODE_RX = /^\d{4}\s?[A-Za-z]{2}$/;
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function NieuweKlantModal({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const createCustomer = useKlantenStore((s) => s.createCustomer);

  const [step, setStep] = useState<ModalStep>('type');
  const [type, setType] = useState<CustomerType | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicateCustomerMatch[]>([]);

  if (!isOpen) return null;

  const reset = () => {
    setStep('type');
    setType(null);
    setForm(EMPTY_FORM);
    setError(null);
    setDuplicates([]);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const pickType = (t: CustomerType) => {
    setType(t);
    setStep('form');
    setError(null);
    setDuplicates([]);
  };

  const backToType = () => {
    setStep('type');
    setError(null);
    setDuplicates([]);
  };

  const setField = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error) setError(null);
    if (duplicates.length) setDuplicates([]);
  };

  const validate = (): string | null => {
    if (!type) return 'Kies eerst een type';
    if (!form.lastName.trim()) {
      return type === 'Person' ? 'Achternaam is verplicht' : 'Achternaam contactpersoon is verplicht';
    }
    if (type === 'Business' && !form.companyName.trim()) {
      return 'Bedrijfsnaam is verplicht';
    }
    const hasContact = !!(form.email.trim() || form.phone.trim() || form.mobile.trim());
    if (!hasContact) return 'Email of telefoonnummer is verplicht';
    if (form.email.trim() && !EMAIL_RX.test(form.email.trim())) {
      return 'Ongeldig email-adres';
    }
    if (form.postcode.trim() && !POSTCODE_RX.test(form.postcode.trim())) {
      return 'Postcode formaat: 1234 AB';
    }
    return null;
  };

  const submit = async (skipDup: boolean) => {
    if (!type) return;
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    setError(null);

    const input: CreateCustomerInput =
      type === 'Person'
        ? {
            type: 'Person',
            firstName: form.firstName.trim() || undefined,
            lastName: form.lastName.trim(),
            email: form.email.trim() || undefined,
            phone: form.phone.trim() || undefined,
            mobile: form.mobile.trim() || undefined,
            street: form.street.trim() || undefined,
            postcode: form.postcode.trim() || undefined,
            city: form.city.trim() || undefined,
            skipDuplicateCheck: skipDup,
          }
        : {
            type: 'Business',
            companyName: form.companyName.trim(),
            firstName: form.firstName.trim() || undefined,
            lastName: form.lastName.trim(),
            email: form.email.trim() || undefined,
            phone: form.phone.trim() || undefined,
            mobile: form.mobile.trim() || undefined,
            street: form.street.trim() || undefined,
            postcode: form.postcode.trim() || undefined,
            city: form.city.trim() || undefined,
            skipDuplicateCheck: skipDup,
          };

    const result = await createCustomer(input);
    setSubmitting(false);

    if (result.ok) {
      handleClose();
      navigate(`/klanten/${result.accountId}?type=account`);
      return;
    }

    if (result.code === 'DUPLICATE_FOUND' && result.duplicates?.length) {
      setDuplicates(result.duplicates);
      setError(null);
      return;
    }

    setError(result.error);
  };

  const openDuplicate = (d: DuplicateCustomerMatch) => {
    handleClose();
    const target = d.sobjectType === 'Lead' ? 'lead' : 'account';
    navigate(`/klanten/${d.sobjectId}?type=${target}`);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={handleClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-8 pointer-events-none">
        <div className="bg-white rounded-lg shadow-xl max-w-xl w-full pointer-events-auto max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {step === 'form' && (
                <button
                  type="button"
                  onClick={backToType}
                  className="text-sm text-slate-500 hover:text-slate-900"
                >
                  ←
                </button>
              )}
              <h2 className="text-base font-semibold text-slate-900">
                {step === 'type' && 'Nieuwe klant'}
                {step === 'form' && type === 'Person' && 'Nieuwe particulier'}
                {step === 'form' && type === 'Business' && 'Nieuw bedrijf'}
              </h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="text-slate-400 hover:text-slate-600"
              aria-label="Sluiten"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {step === 'type' && <TypePicker onPick={pickType} />}
            {step === 'form' && type && (
              <FormBody
                type={type}
                form={form}
                setField={setField}
                error={error}
                duplicates={duplicates}
                onOpenDuplicate={openDuplicate}
              />
            )}
          </div>

          {/* Footer */}
          {step === 'form' && (
            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-1.5 text-sm text-slate-600 hover:text-slate-900"
                disabled={submitting}
              >
                Annuleren
              </button>
              {duplicates.length > 0 ? (
                <button
                  type="button"
                  onClick={() => submit(true)}
                  disabled={submitting}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50"
                >
                  {submitting ? 'Bezig...' : 'Toch nieuw aanmaken'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => submit(false)}
                  disabled={submitting}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {submitting ? 'Bezig...' : 'Klant aanmaken'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Type-keuze (stap 1) ────────────────────────────────────────────────

function TypePicker({ onPick }: { onPick: (t: CustomerType) => void }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <TypeCard
        icon={
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
        }
        title="Particulier"
        description="Eén persoon, eigen email en adres"
        onClick={() => onPick('Person')}
      />
      <TypeCard
        icon={
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
          </svg>
        }
        title="Zakelijk"
        description="Bedrijf met contactpersoon"
        onClick={() => onPick('Business')}
      />
    </div>
  );
}

function TypeCard({
  icon,
  title,
  description,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group border border-slate-200 rounded-lg p-5 text-left hover:border-indigo-500 hover:bg-indigo-50/40 transition-colors"
    >
      <div className="w-10 h-10 rounded-md bg-slate-100 group-hover:bg-indigo-100 flex items-center justify-center text-slate-600 group-hover:text-indigo-600 mb-3 transition-colors">
        {icon}
      </div>
      <div className="text-sm font-semibold text-slate-900 mb-1">{title}</div>
      <div className="text-xs text-slate-500">{description}</div>
    </button>
  );
}

// ─── Form body (stap 2) ─────────────────────────────────────────────────

interface FormBodyProps {
  type: CustomerType;
  form: FormState;
  setField: (k: keyof FormState, v: string) => void;
  error: string | null;
  duplicates: DuplicateCustomerMatch[];
  onOpenDuplicate: (d: DuplicateCustomerMatch) => void;
}

function FormBody({ type, form, setField, error, duplicates, onOpenDuplicate }: FormBodyProps) {
  return (
    <div className="space-y-5">
      {duplicates.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="font-medium text-amber-900 mb-1">
            Mogelijke duplicaten gevonden
          </div>
          <ul className="space-y-1.5 text-amber-800">
            {duplicates.map((d) => (
              <li key={d.sobjectId} className="flex items-center justify-between gap-2">
                <span className="truncate">
                  <span className="font-medium">{d.name || '(naamloos)'}</span>
                  {d.email && <span className="text-amber-700"> — {d.email}</span>}
                </span>
                <button
                  type="button"
                  onClick={() => onOpenDuplicate(d)}
                  className="text-xs font-medium text-amber-900 underline hover:no-underline whitespace-nowrap"
                >
                  Open
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-2 text-xs text-amber-700">
            Klik <span className="font-medium">Toch nieuw aanmaken</span> om te bevestigen dat dit een nieuwe klant is.
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {type === 'Business' && (
        <Section title="Bedrijf">
          <Field
            label="Bedrijfsnaam"
            required
            value={form.companyName}
            onChange={(v) => setField('companyName', v)}
            placeholder="Bedrijfsnaam BV"
          />
        </Section>
      )}

      <Section title={type === 'Business' ? 'Contactpersoon' : 'Naam'}>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Voornaam"
            value={form.firstName}
            onChange={(v) => setField('firstName', v)}
          />
          <Field
            label="Achternaam"
            required
            value={form.lastName}
            onChange={(v) => setField('lastName', v)}
          />
        </div>
        <Field
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => setField('email', v)}
          placeholder="naam@voorbeeld.nl"
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Telefoon"
            type="tel"
            value={form.phone}
            onChange={(v) => setField('phone', v)}
            placeholder="0521-555000"
          />
          {type === 'Person' && (
            <Field
              label="Mobiel"
              type="tel"
              value={form.mobile}
              onChange={(v) => setField('mobile', v)}
              placeholder="06-12345678"
            />
          )}
        </div>
      </Section>

      <Section title="Adres">
        <Field
          label="Straat + huisnummer"
          value={form.street}
          onChange={(v) => setField('street', v)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Postcode"
            value={form.postcode}
            onChange={(v) => setField('postcode', v)}
            placeholder="1234 AB"
          />
          <Field
            label="Plaats"
            value={form.city}
            onChange={(v) => setField('city', v)}
          />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: 'text' | 'email' | 'tel';
  placeholder?: string;
}

function Field({ label, value, onChange, required, type = 'text', placeholder }: FieldProps) {
  return (
    <label className="block">
      <div className={clsx('text-xs font-medium mb-1', required ? 'text-slate-700' : 'text-slate-500')}>
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
      />
    </label>
  );
}
